import { _ModuleSupport } from 'ag-charts-community';
import type { AxisID, FeatureCollection, Position } from 'ag-charts-core';
import { ChartAxisDirection, Property, createId } from 'ag-charts-core';
import type { AgTopologyChartOptions } from 'ag-charts-types';

import type { LonLatBBox } from '../series/map-util/lonLatBbox';
import type { ITopology } from '../series/map-util/topologyTypes';

const { Chart, MercatorScale } = _ModuleSupport;
function isTopologySeries(
    series: _ModuleSupport.Series<_ModuleSupport.DatumIndexType, any, object, any>
): series is ITopology {
    return (
        series.type === 'map-shape' ||
        series.type === 'map-line' ||
        series.type === 'map-marker' ||
        series.type === 'map-shape-background' ||
        series.type === 'map-line-background'
    );
}

export class TopologyChart extends Chart {
    static override readonly className = 'TopologyChart';
    static readonly type = 'topology' as const;
    private readonly xAxis = { id: createId<AxisID>(_ModuleSupport.Axis), direction: ChartAxisDirection.X } as const;
    private readonly yAxis = { id: createId<AxisID>(_ModuleSupport.Axis), direction: ChartAxisDirection.Y } as const;

    @Property
    topology?: FeatureCollection;

    constructor(options: _ModuleSupport.ChartOptions, resources?: _ModuleSupport.TransferableResources) {
        super(options, resources);

        if (this.ctx.zoomManager) {
            this.ctx.zoomManager.setAxes([this.xAxis, this.yAxis]);
            this.ctx.zoomManager.panToBBoxScalingMode =
                _ModuleSupport.PanToBBoxScalingModeEnum.WhenViewportTooSmallScaleXYProportionally;
        }
    }

    override getChartType() {
        return 'topology' as const;
    }

    override updateData() {
        super.updateData();

        const options = this.getOptions() as AgTopologyChartOptions;
        if (this.topology !== options.topology) {
            this.topology = options.topology;
        }

        const { topology } = this;
        for (const series of this.series) {
            if (isTopologySeries(series)) {
                series.setChartTopology(topology);
            }
        }
    }

    protected performLayout(ctx: _ModuleSupport.LayoutContext) {
        const { seriesRoot, annotationRoot } = this;

        const seriesRect = ctx.layoutBox.clone().shrink(this.seriesArea.getPadding());

        this.seriesRect = seriesRect;
        this.animationRect = seriesRect;

        const mapSeries = this.series as ITopology[];
        const combinedBbox = mapSeries.reduce<LonLatBBox | undefined>((combined, series) => {
            if (!series.visible) return combined;
            const bbox = series.topologyBounds;
            if (bbox == null) return combined;
            if (combined == null) return bbox;
            return combined.merge(bbox);
        }, undefined);

        let scale: _ModuleSupport.MercatorScale | undefined;
        if (combinedBbox != null) {
            const { lon0, lat0, lon1, lat1 } = combinedBbox;
            const domain: Position[] = [
                [lon0, lat0],
                [lon1, lat1],
            ];
            const bounds = MercatorScale.bounds(domain);
            const { width, height } = seriesRect;

            const viewBoxScale = Math.min(width / bounds.width, height / bounds.height);

            const viewBoxWidth = bounds.width * viewBoxScale;
            const viewBoxHeight = bounds.height * viewBoxScale;
            const viewBoxOriginX = (width - viewBoxWidth) / 2;
            const viewBoxOriginY = (height - viewBoxHeight) / 2;

            const x0 = viewBoxOriginX;
            const y0 = viewBoxOriginY;
            const x1 = viewBoxOriginX + viewBoxWidth;
            const y1 = viewBoxOriginY + viewBoxHeight;

            const zoom = this.ctx.chartState.getValue('zoom');
            const xZoom = zoom?.x ?? { min: 0, max: 1 };
            const yZoom = zoom?.y ?? { min: 0, max: 1 };
            const xSpan = (x1 - x0) / (xZoom.max - xZoom.min);
            const xStart = x0 - xSpan * xZoom.min;
            const ySpan = (y1 - y0) / (1 - yZoom.min - (1 - yZoom.max));
            const yStart = y0 - ySpan * (1 - yZoom.max);

            scale = new MercatorScale(domain, [
                [xStart, yStart],
                [xStart + xSpan, yStart + ySpan],
            ]);
        }

        for (const series of mapSeries) {
            series.scale = scale;
        }

        const seriesVisible = this.series.some((s) => s.visible);
        seriesRoot.visible = seriesVisible;
        for (const group of [seriesRoot, annotationRoot]) {
            group.translationX = Math.floor(seriesRect.x);
            group.translationY = Math.floor(seriesRect.y);
            group.setClipRect(seriesRect.clone());
        }

        this.ctx.layoutManager.emitLayoutComplete(ctx, {
            series: { visible: seriesVisible, rect: seriesRect, paddedRect: ctx.layoutBox },
            layoutBox: ctx.layoutBox,
        });
    }

    override hasPgUpPgDownSupport(): boolean {
        return false;
    }
}
